import React from "react";
import { GraduationCap, LogOut, CircleDot } from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";

// Read-only enrollment history timeline, visible to all staff.
// Renders each enrollment period (dates enrolled → left) plus the "away" gaps
// between periods and the reason for leaving when recorded.

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export function EnrollmentTimeline({ periods }) {
  const list = (periods || [])
    .filter((p) => p && (p.start_date || p.end_date))
    .slice()
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));

  return (
    <CollapsibleSection id="enrollment-history" title="Enrollment History" color="slate" testid="enrollment-timeline">
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="enrollment-timeline-empty">
          No enrollment history recorded yet.
        </p>
      ) : (
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
          {list.map((p, i) => {
            const open = !p.end_date;
            const next = list[i + 1];
            return (
              <React.Fragment key={p.id || i}>
                <li className="ml-5" data-testid={`enrollment-period-${i}`}>
                  <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${open ? "bg-green-500" : "bg-slate-400"}`}>
                    <CircleDot className="h-2.5 w-2.5 text-white" />
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">
                      {fmt(p.start_date) || "Unknown start"} – {open ? "Present" : fmt(p.end_date)}
                    </span>
                    {open ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Enrolled</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">Left</span>
                    )}
                  </div>
                  {!open && p.reason ? (
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1" data-testid={`enrollment-period-${i}-reason`}>
                      <LogOut className="h-3 w-3" /> Reason: {p.reason}
                    </p>
                  ) : null}
                </li>
                {next && p.end_date && next.start_date && next.start_date > p.end_date ? (
                  <li className="ml-5" data-testid={`enrollment-away-${i}`}>
                    <span className="absolute -left-[7px] h-3 w-3 rounded-full border-2 border-slate-300 bg-white" />
                    <p className="text-xs text-amber-700 italic flex items-center gap-1">
                      <GraduationCap className="h-3 w-3" /> Away {fmt(p.end_date)} – {fmt(next.start_date)}
                    </p>
                  </li>
                ) : null}
              </React.Fragment>
            );
          })}
        </ol>
      )}
    </CollapsibleSection>
  );
}
